'use client'

import { createClient } from '@/lib/supabase/client'
import type { AuthUser, AuthSession, SignUpData, SignInData, AuthResponse } from '@/types/auth'
import type { AuthError } from '@supabase/supabase-js'

class AuthService {
    private supabase: ReturnType<typeof createClient>

    constructor() {
        this.supabase = createClient()
    }

    /**
     * Sign up a new user with email confirmation via OTP
     */
    async signUp(data: SignUpData): Promise<AuthResponse> {
        try {
            const redirectUrl = process.env['NEXT_PUBLIC_APP_URL'] || window.location.origin

            const { data: authData, error } = await this.supabase.auth.signUp({
                email: data.email,
                password: data.password,
                options: {
                    emailRedirectTo: `${redirectUrl}/auth/callback`,
                    data: {
                        first_name: data.firstName,
                        last_name: data.lastName,
                    },
                },
            })

            return {
                user: authData.user as AuthUser,
                session: authData.session as AuthSession,
                error,
            }
        } catch (error) {
            return {
                user: null,
                session: null,
                error: error as AuthError,
            }
        }
    }

    /**
     * Sign in an existing user
     */
    async signIn(data: SignInData): Promise<AuthResponse> {
        try {
            const { data: authData, error } = await this.supabase.auth.signInWithPassword({
                email: data.email,
                password: data.password,
            })

            return {
                user: authData.user as AuthUser,
                session: authData.session as AuthSession,
                error,
            }
        } catch (error) {
            return {
                user: null,
                session: null,
                error: error as AuthError,
            }
        }
    }

    /**
     * Sign out the current user
     */
    async signOut(): Promise<{ error: AuthError | null }> {
        try {
            const { error } = await this.supabase.auth.signOut()
            return { error }
        } catch (error) {
            return { error: error as AuthError }
        }
    }

    /**
     * Send a password reset email
     */
    async resetPassword(email: string): Promise<{ error: AuthError | null }> {
        try {
            const { error } = await this.supabase.auth.resetPasswordForEmail(email)
            return { error }
        } catch (error) {
            return { error: error as AuthError }
        }
    }

    /**
     * Get the current session
     */
    async getSession(): Promise<{ session: AuthSession | null; error: AuthError | null }> {
        try {
            const { data, error } = await this.supabase.auth.getSession()
            return {
                session: data.session as AuthSession,
                error,
            }
        } catch (error) {
            return {
                session: null,
                error: error as AuthError,
            }
        }
    }

    /**
     * Get the current user
     */
    async getUser(): Promise<{ user: AuthUser | null; error: AuthError | null }> {
        try {
            const { data, error } = await this.supabase.auth.getUser()
            return {
                user: data.user as AuthUser,
                error,
            }
        } catch (error) {
            return {
                user: null,
                error: error as AuthError,
            }
        }
    }

    /**
     * Listen to auth state changes
     */
    onAuthStateChange(callback: (user: AuthUser | null) => void) {
        return this.supabase.auth.onAuthStateChange((event, session) => {
            callback((session?.user as AuthUser) || null)
        })
    }

    /**
     * Sign in with OAuth providers
     */
    async signInWithOAuth(provider: 'google' | 'github', nextUrl?: string) {
        try {
            const redirectUrl = process.env['NEXT_PUBLIC_APP_URL'] || window.location.origin
            const callbackUrl = `${redirectUrl}/auth/callback`
            const finalRedirectUrl = nextUrl
                ? `${callbackUrl}?next=${encodeURIComponent(nextUrl)}`
                : callbackUrl

            const { data, error } = await this.supabase.auth.signInWithOAuth({
                provider,
                options: {
                    redirectTo: finalRedirectUrl,
                },
            })
            return { data, error }
        } catch (error) {
            return { data: null, error: error as AuthError }
        }
    }

    /**
     * Send OTP to email
     */
    async signInWithOtp(email: string): Promise<{ error: AuthError | null }> {
        try {
            const { error } = await this.supabase.auth.signInWithOtp({
                email,
                options: {
                    shouldCreateUser: false,
                },
            })
            return { error }
        } catch (error) {
            return { error: error as AuthError }
        }
    }

    /**
     * Verify OTP code for email verification
     * Use 'signup' for new user email verification during signup
     * Use 'email' for existing user email verification during login
     */
    async verifyOtp(data: {
        email: string
        token: string
        type: 'signup' | 'email' | 'recovery' | 'email_change'
    }): Promise<AuthResponse> {
        try {
            const { data: authData, error } = await this.supabase.auth.verifyOtp({
                email: data.email,
                token: data.token,
                type: data.type,
            })

            return {
                user: authData.user as AuthUser,
                session: authData.session as AuthSession,
                error,
            }
        } catch (error) {
            return {
                user: null,
                session: null,
                error: error as AuthError,
            }
        }
    }

    /**
     * Resend OTP
     */
    async resendOtp(email: string): Promise<{ error: AuthError | null }> {
        try {
            const { error } = await this.supabase.auth.resend({
                type: 'signup',
                email,
            })
            return { error }
        } catch (error) {
            return { error: error as AuthError }
        }
    }
}

// Export a singleton instance
export const authService = new AuthService()
